import './operationResultPopup.scss';

import { Button, Popup, TransparentButton } from '@approvalmax/ui';
import { errorHelpers, miscHelpers } from '@approvalmax/utils';
import { actions, selectors, ui } from 'modules/common';
import ImageStop from 'modules/components/ImageIcons/ImageStop';
import { domain, State } from 'modules/data';
import { useShallowEqualSelector } from 'modules/react-redux';
import { CloseIcon } from 'modules/sprites';
import { clearPersistStore } from 'modules/store';
import { memo, useCallback } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';

import { cancelActivePopup, showMatchingPopup } from '../actions';
import { OperationResult, OperationResultPopupData } from '../reducers/page/activePopup/operationResultPopupReducer';
import { getActivePopup } from '../selectors/pageSelectors';
import { messages } from './OperationResultPopup.messages';

const bem = bemFactory.block('reql-operation-result-popup');
const qa = bemFactory.qa('reql-operation-result-popup');

enum BadgeTheme {
    Green = 'green',
    Red = 'red',
}

const OperationResultPopup = () => {
    const dispatch = useDispatch();
    const { result, request, stepId, me, allUsers } = useShallowEqualSelector((state: State) => {
        const popupData = getActivePopup<OperationResultPopupData>(state);

        return {
            result: popupData.result,
            request: selectors.request.findRequestById(state, popupData.requestId),
            stepId: popupData.stepId,
            me: selectors.profile.getProfileUser(state),
            allUsers: selectors.user.getUsers(state),
        };
    });

    const onLogoutButtonClick = useCallback(() => {
        dispatch(actions.logout(clearPersistStore));
    }, [dispatch]);

    const renderSuccessPopup = () => {
        let badgeTheme;
        let title;
        let badgeText;

        switch (result) {
            case OperationResult.SuccessApproved:
                badgeTheme = BadgeTheme.Green;
                badgeText = messages.ApprovedBadge;
                title = messages.approvedTitle({
                    requestName: request?.displayName || '',
                    br: <br />,
                });
                break;

            case OperationResult.SuccessRejected:
                badgeTheme = BadgeTheme.Red;
                badgeText = messages.rejectedBadge;
                title = messages.rejectedTitle({
                    requestName: request?.displayName || '',
                    br: <br />,
                });
                break;

            default:
                throw errorHelpers.notImplementedError();
        }

        return (
            <Popup.EmptyContent className={bem()} qa={qa()}>
                <div className={bem('success-popup-body')}>
                    {renderCrossCloseButton()}

                    <div className={bem('success-popup-badge-section')}>
                        <div className={bem('success-popup-badge', badgeTheme)} data-qa={qa('badge')}>
                            {badgeText}
                        </div>
                    </div>

                    <div className={bem('success-popup-bottom-section')}>
                        <div className={bem('success-popup-title')} data-qa={qa('title')}>
                            {title}
                        </div>

                        <Button colorTheme='grey' qa={qa('close-button')} execute={() => dispatch(cancelActivePopup())}>
                            {messages.closeButtonText}
                        </Button>
                    </div>
                </div>
            </Popup.EmptyContent>
        );
    };

    const renderRequestNotFoundPopup = () => {
        return (
            <Popup.EmptyContent className={bem()} qa={qa()}>
                <div className={bem('request-not-found-popup-body')}>
                    {renderCrossCloseButton()}

                    <div className={bem('request-not-found-popup-upper-section')}>
                        <div className={bem('request-not-found-popup-title')} data-qa={qa('title')}>
                            {messages.requestNotFoundTitle}
                        </div>

                        <div className={bem('request-not-found-popup-description')} data-qa={qa('description')}>
                            {messages.requestNotFoundDescription}
                        </div>

                        <ui.Avatar className={bem('request-not-found-popup-me-avatar')} user={me} size={40} />

                        <div className={bem('request-not-found-popup-me-name')}>{me.displayName}</div>

                        <div className={bem('request-not-found-popup-me-email')}>{me.userEmail}</div>
                    </div>

                    <div className={bem('request-not-found-popup-bottom-section')}>
                        <Button colorTheme='grey' qa={qa('logout-button')} execute={onLogoutButtonClick}>
                            {messages.logoutButtonText}
                        </Button>
                    </div>
                </div>
            </Popup.EmptyContent>
        );
    };

    const renderGeneralFailurePopup = () => {
        let title;
        let description = messages.generalFailureDescription;

        switch (result) {
            case OperationResult.FailedRequestExternallyUpdated:
                title = messages.externallyUpdatedTitle;
                break;

            case OperationResult.FailedNotAnApprover:
                title = messages.notApproverTitle;
                break;

            case OperationResult.FailedRequestClosedApproved:
                title = messages.closedApprovedTitle;
                break;

            case OperationResult.FailedRequestClosedRejected:
                title = messages.closedRejectedTitle;
                break;

            case OperationResult.FailedRequestCancelled:
                title = messages.cancelledTitle;
                break;

            case OperationResult.FailedRequestAlreadyApproved:
                title = messages.alreadyApprovedTitle;
                break;

            case OperationResult.FailedRequestAlreadyRejected:
                title = messages.alreadyRejectedTitle;
                break;

            case OperationResult.FailedRequestStepNotStarted:
                title = messages.stepNotStartedTitle;
                break;

            case OperationResult.FailedRequestStepDone: {
                const step = request!.steps.find((s) => s.id === stepId)!;

                miscHelpers.invariant(step, 'Step must be provided for this type of OperationResult.');

                const participantInfo = step.participants.find(
                    (p) => p.decision === domain.RequestStepParticipantDecision.Approve
                )!;
                const approver = allUsers.find((u) => u.id === participantInfo.userId)!;

                title = messages.stepDoneTitle({
                    user: approver.displayName,
                });
                break;
            }

            case OperationResult.FailedRequestRequiresEditing:
                title = messages.requiresEditingTitle;
                break;

            case OperationResult.FailedRequestHasMatchingError: {
                title = messages.hasMatchingErrorTitle;
                break;
            }

            case OperationResult.FailedRequestMatchedPOsBalanceIsExceeded: {
                title = messages.hasMatchingOverbudgetErrorTitle;
                description = messages.hasMatchingOverbudgetErrorDescription;
                break;
            }

            case OperationResult.FailedRequestAllAllocationsExceedBillAmount: {
                title = messages.hasMatchingExcessPOBalanceErrorTitle;
                description = messages.hasMatchingExcessPOBalanceErrorDescription;
                break;
            }

            default:
                throw errorHelpers.notSupportedError();
        }

        return (
            <Popup.EmptyContent className={bem()} qa={qa()}>
                <div className={bem('general-failure-popup-body')}>
                    {renderCrossCloseButton()}

                    <ImageStop />

                    <div className={bem('general-failure-popup-title')} data-qa={qa('title')}>
                        {title}
                    </div>

                    {description && (
                        <div className={bem('general-failure-popup-description')} data-qa={qa('description')}>
                            {description}
                        </div>
                    )}

                    {renderActions()}
                </div>
            </Popup.EmptyContent>
        );
    };

    const openMatchingPopup = () => {
        dispatch(showMatchingPopup());
    };

    const renderActions = () => {
        let actions;

        switch (result) {
            case OperationResult.FailedRequestMatchedPOsBalanceIsExceeded:
            case OperationResult.FailedRequestAllAllocationsExceedBillAmount:
                actions = (
                    <Button qa={qa('open-matching')} execute={openMatchingPopup}>
                        {messages.openMatchingPopupButtonText}
                    </Button>
                );
        }

        return actions;
    };

    const renderCrossCloseButton = () => {
        return (
            <TransparentButton
                className={bem('cross-close-button')}
                qa={qa('close-button')}
                execute={() => dispatch(cancelActivePopup())}
            >
                <CloseIcon width={14} height={14} />
            </TransparentButton>
        );
    };

    switch (result) {
        case OperationResult.SuccessApproved:
        case OperationResult.SuccessRejected:
            return renderSuccessPopup();

        case OperationResult.FailedRequestNotFound:
            return renderRequestNotFoundPopup();

        default:
            return renderGeneralFailurePopup();
    }
};

export default memo(OperationResultPopup);
